import { useState, useRef } from 'react'
import { Image, Upload, Trash2, X, Loader2, GripVertical, ZoomIn } from 'lucide-react'
import api from '../../core/services/api'
import ConfirmModal from '../ConfirmModal'

interface Foto {
  id: string
  titolo: string
  filename: string
  file_size: number
  content_type: string
  ordine: number
  created_at: string
}

interface FotoSectionProps {
  offertaId: string
  foto: Foto[]
  onFotoChange: () => void
  disabled?: boolean
}

export default function FotoSection({ offertaId, foto, onFotoChange, disabled }: FotoSectionProps) {
  const [uploading, setUploading] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [draggedId, setDraggedId] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files || files.length === 0) return

    setUploading(true)
    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i]
        if (!file.type.startsWith('image/')) continue

        const formData = new FormData()
        formData.append('file', file)
        formData.append('titolo', file.name)

        await api.post(`/v1/offerte/${offertaId}/foto`, formData)
      }
      onFotoChange()
    } catch (error) {
      console.error('Errore upload foto:', error)
    } finally {
      setUploading(false)
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    }
  }

  const handleDelete = async (fotoId: string) => {
    try {
      await api.delete(`/v1/offerte/${offertaId}/foto/${fotoId}`)
      onFotoChange()
    } catch (error) {
      console.error('Errore eliminazione foto:', error)
    }
    setConfirmDelete(null)
  }

  const handleDragStart = (e: React.DragEvent, fotoId: string) => {
    setDraggedId(fotoId)
    e.dataTransfer.effectAllowed = 'move'
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
  }

  const handleDrop = async (e: React.DragEvent, targetId: string) => {
    e.preventDefault()
    if (!draggedId || draggedId === targetId) {
      setDraggedId(null)
      return
    }

    // Riordina localmente
    const newOrder = [...foto]
    const draggedIndex = newOrder.findIndex((f) => f.id === draggedId)
    const targetIndex = newOrder.findIndex((f) => f.id === targetId)

    if (draggedIndex !== -1 && targetIndex !== -1) {
      const [removed] = newOrder.splice(draggedIndex, 1)
      newOrder.splice(targetIndex, 0, removed)

      // Invia nuovo ordine al backend
      try {
        await api.put(`/v1/offerte/${offertaId}/foto/ordine`, {
          foto_ids: newOrder.map((f) => f.id),
        })
        onFotoChange()
      } catch (error) {
        console.error('Errore riordino foto:', error)
      }
    }

    setDraggedId(null)
  }

  const handlePreview = async (fotoId: string) => {
    try {
      // Usa l'endpoint allegati per scaricare
      const res = await api.get(`/v1/allegati/${fotoId}/download`, {
        responseType: 'blob',
      })
      const url = URL.createObjectURL(res.data)
      setPreviewUrl(url)
    } catch (error) {
      console.error('Errore preview:', error)
    }
  }

  const closePreview = () => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl)
      setPreviewUrl(null)
    }
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Image className="w-5 h-5 text-primary-600" />
          <h3 className="font-semibold text-gray-900">Foto</h3>
          <span className="text-sm text-gray-400">({foto.length})</span>
        </div>
        {!disabled && (
          <div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={handleFileSelect}
              className="hidden"
              id="foto-upload"
            />
            <label
              htmlFor="foto-upload"
              className="inline-flex items-center gap-2 px-3 py-1.5 text-sm bg-primary-50 text-primary-600 rounded-lg hover:bg-primary-100 cursor-pointer"
            >
              {uploading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Upload className="w-4 h-4" />
              )}
              Aggiungi Foto
            </label>
          </div>
        )}
      </div>

      {foto.length === 0 ? (
        <div className="text-center py-8 text-gray-400">
          <Image className="w-12 h-12 mx-auto mb-2 opacity-50" />
          <p className="text-sm">Nessuna foto caricata</p>
          {!disabled && (
            <p className="text-xs mt-1">Trascina o clicca per aggiungere immagini</p>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {foto.map((f) => (
            <div
              key={f.id}
              draggable={!disabled}
              onDragStart={(e) => handleDragStart(e, f.id)}
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, f.id)}
              className={`relative group aspect-square bg-gray-100 rounded-lg overflow-hidden border-2 ${
                draggedId === f.id ? 'border-primary-400 opacity-50' : 'border-transparent'
              } ${!disabled ? 'cursor-move' : ''}`}
            >
              {/* Thumbnail */}
              <div
                className="w-full h-full bg-gray-200 flex items-center justify-center"
                onClick={() => handlePreview(f.id)}
              >
                <Image className="w-8 h-8 text-gray-400" />
              </div>

              {/* Overlay */}
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                <button
                  onClick={() => handlePreview(f.id)}
                  className="p-2 bg-white rounded-full text-gray-700 hover:bg-gray-100 mx-1"
                  title="Ingrandisci"
                >
                  <ZoomIn className="w-4 h-4" />
                </button>
                {!disabled && (
                  <button
                    onClick={() => setConfirmDelete(f.id)}
                    className="p-2 bg-white rounded-full text-red-500 hover:bg-red-50 mx-1"
                    title="Elimina"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>

              {/* Drag handle */}
              {!disabled && (
                <div className="absolute top-1 left-1 p-1 bg-white/80 rounded opacity-0 group-hover:opacity-100">
                  <GripVertical className="w-3 h-3 text-gray-400" />
                </div>
              )}

              {/* Filename */}
              <div className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-xs p-1 truncate">
                {f.titolo || f.filename}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Preview Modal */}
      {previewUrl && (
        <div
          className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
          onClick={closePreview}
        >
          <button
            onClick={closePreview}
            className="absolute top-4 right-4 p-2 bg-white/10 rounded-full text-white hover:bg-white/20"
          >
            <X className="w-6 h-6" />
          </button>
          <img
            src={previewUrl}
            alt="Preview"
            className="max-w-full max-h-full object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      {/* Confirm Delete */}
      <ConfirmModal
        isOpen={!!confirmDelete}
        title="Elimina Foto"
        message="Sei sicuro di voler eliminare questa foto?"
        confirmLabel="Elimina"
        onConfirm={() => confirmDelete && handleDelete(confirmDelete)}
        onCancel={() => setConfirmDelete(null)}
      />
    </div>
  )
}
